// Tiny inline-edit binding for `.editable` elements emitted by the
// `application_helper#editable` helper.
//
// Each .editable element carries data-table, data-model, data-id, data-field.
// Clicking it swaps the rendered value for an <input>; pressing Enter (or
// blurring) PATCHes /<table>/:id with `<model>[<field>]=value` and reloads
// the page so the freshly formatted value is rendered server-side. Escape
// cancels without saving.
//
// If the element carries data-options="A|B|C" the inline editor renders a
// <select> over those choices instead of a free-text input (handy for
// status / role columns).

const INPUT_CLS =
  "inline-block w-[min(180px,90%)] rounded border border-[var(--color-brand-500)] bg-black/40 px-1.5 py-0.5 text-sm tabular-nums text-[var(--color-ink)] outline-none focus:shadow-[0_0_0_2px_var(--color-brand-ring)]"
const SELECT_CLS =
  "inline-block min-w-[110px] rounded border border-[var(--color-brand-500)] bg-black/40 px-1.5 py-0.5 text-sm text-[var(--color-ink)] outline-none cursor-pointer"

function startEdit(el: HTMLElement): void {
  if (el.querySelector("input, select")) return

  const original = el.textContent?.trim() ?? ""
  el.dataset.original = original
  const options = el.dataset.options
  const isSelect = !!options

  const field: HTMLInputElement | HTMLSelectElement = isSelect
    ? document.createElement("select")
    : document.createElement("input")

  if (isSelect) {
    const select = field as HTMLSelectElement
    select.className = SELECT_CLS
    options!.split("|").forEach((label) => {
      const o = document.createElement("option")
      o.value = label
      o.textContent = label
      if (label === original) o.selected = true
      select.appendChild(o)
    })
  } else {
    const input = field as HTMLInputElement
    input.type = "text"
    input.value = original
    input.className = INPUT_CLS
  }

  el.innerHTML = ""
  el.appendChild(field)
  field.focus()
  if (!isSelect) (field as HTMLInputElement).select()

  let saving = false
  const cleanup = () => {
    // Cast — `field` is `HTMLInputElement | HTMLSelectElement` and TS
    // doesn't apply the typed `addEventListener` overload across a union.
    field.removeEventListener("keydown", onKey as EventListener)
    field.removeEventListener("blur", onBlur)
    if (isSelect) field.removeEventListener("change", onChange)
  }

  const cancel = () => {
    cleanup()
    el.textContent = original
  }

  const save = async () => {
    if (saving) return
    const newVal = (field as HTMLInputElement | HTMLSelectElement).value
    if (newVal === original) {
      cancel()
      return
    }
    saving = true
    cleanup()
    el.textContent = "…"

    const csrf =
      (document.querySelector("meta[name=csrf-token]") as HTMLMetaElement | null)?.content ?? ""
    const { table, model, id, field: attr } = el.dataset
    if (!table || !model || !id || !attr) {
      el.textContent = original
      return
    }

    const fd = new FormData()
    fd.append(`${model}[${attr}]`, newVal)
    fd.append("_method", "patch")
    fd.append("authenticity_token", csrf)

    try {
      const res = await fetch(`/${table}/${id}`, {
        method: "POST",
        body: fd,
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest", Accept: "text/html, application/json" },
      })
      if (res.ok || res.redirected) {
        window.location.reload()
      } else {
        el.textContent = original
      }
    } catch (_e) {
      el.textContent = original
    }
  }

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      save()
    } else if (e.key === "Escape") {
      e.preventDefault()
      cancel()
    }
  }
  const onBlur = () => save()
  const onChange = () => save()

  field.addEventListener("keydown", onKey as EventListener)
  field.addEventListener("blur", onBlur)
  if (isSelect) field.addEventListener("change", onChange)
}

// Delegated through document so it survives Turbo navigations.
document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement
  // Don't hijack clicks on links inside an editable cell.
  if (target.closest("a")) return
  const editable = target.closest<HTMLElement>(".editable")
  if (editable) startEdit(editable)
})
